import React from "react";
import {
  Box,
  Typography,
  Divider,
  Card,
  CardContent,
  Grid2 as Grid,
  Chip,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
} from "@mui/material";
import {
  Info as InfoIcon,
  Schedule as ScheduleIcon,
  Message as MessageIcon,
  Fingerprint as FingerprintIcon,
  Business as BusinessIcon,
  Person as PersonIcon,
  Timeline as TimelineIcon,
} from "@mui/icons-material";
import type { BossBabyChatSessionDto } from "@/apis/bossBabyChatLog";

interface SessionInfoTabProps {
  session: BossBabyChatSessionDto;
}

/**
 * Boss Baby 세션 정보 탭 컴포넌트
 *
 * @description Boss Baby 세션의 기본 정보를 표시하는 탭
 * @author Max <sud665@ymjlab.com>
 * @version 1.0.0
 * @since 2025-01-29
 */
export const SessionInfoTab: React.FC<SessionInfoTabProps> = ({ session }) => {
  const getStatusColor = (
    status: string
  ): "default" | "primary" | "error" | "success" => {
    switch (status.toLowerCase()) {
      case "active":
        return "success";
      case "completed":
        return "primary";
      case "expired":
        return "error";
      default:
        return "default";
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h6" gutterBottom>
        Session Information
      </Typography>
      <Divider sx={{ mb: 3 }} />

      <Grid container spacing={3}>
        {/* 기본 세션 정보 */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Card sx={{ height: "100%" }}>
            <CardContent>
              <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                <InfoIcon sx={{ mr: 1, color: "primary.main" }} />
                <Typography variant="h6">Basic Information</Typography>
              </Box>

              <List dense>
                <ListItem>
                  <ListItemIcon>
                    <FingerprintIcon />
                  </ListItemIcon>
                  <ListItemText
                    primary="Session UUID"
                    secondary={session.sessionUuid}
                    secondaryTypographyProps={{
                      sx: { fontFamily: "monospace" },
                    }}
                  />
                </ListItem>

                <ListItem>
                  <ListItemIcon>
                    <InfoIcon />
                  </ListItemIcon>
                  <ListItemText
                    primary="Session ID"
                    secondary={session.sessionId}
                  />
                </ListItem>

                <ListItem>
                  <ListItemIcon>
                    <TimelineIcon />
                  </ListItemIcon>
                  <ListItemText
                    primary="Status"
                    secondary={
                      <Chip
                        label={session.status}
                        color={getStatusColor(session.status)}
                        size="small"
                        sx={{ mt: 0.5 }}
                      />
                    }
                  />
                </ListItem>

                <ListItem>
                  <ListItemIcon>
                    <PersonIcon />
                  </ListItemIcon>
                  <ListItemText
                    primary="User ID"
                    secondary={session.userId || "Not specified"}
                  />
                </ListItem>

                <ListItem>
                  <ListItemIcon>
                    <BusinessIcon />
                  </ListItemIcon>
                  <ListItemText
                    primary="Company ID"
                    secondary={session.comId}
                  />
                </ListItem>
              </List>
            </CardContent>
          </Card>
        </Grid>

        {/* 타임스탬프 정보 */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Card sx={{ height: "100%" }}>
            <CardContent>
              <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                <ScheduleIcon sx={{ mr: 1, color: "primary.main" }} />
                <Typography variant="h6">Timeline</Typography>
              </Box>

              <List dense>
                <ListItem>
                  <ListItemIcon>
                    <ScheduleIcon />
                  </ListItemIcon>
                  <ListItemText
                    primary="Created At"
                    secondary={
                      <Box>
                        <Typography variant="body2">
                          {new Date(session.regDttm).toLocaleString("en-US", {
                            year: "numeric",
                            month: "long",
                            day: "numeric",
                            hour: "2-digit",
                            minute: "2-digit",
                            second: "2-digit",
                          })}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {new Date(session.regDttm).toISOString()}
                        </Typography>
                      </Box>
                    }
                  />
                </ListItem>

                <ListItem>
                  <ListItemIcon>
                    <TimelineIcon />
                  </ListItemIcon>
                  <ListItemText
                    primary="Last Activity"
                    secondary={
                      <Box>
                        <Typography variant="body2">
                          {new Date(session.lastActivity).toLocaleString(
                            "en-US",
                            {
                              year: "numeric",
                              month: "long",
                              day: "numeric",
                              hour: "2-digit",
                              minute: "2-digit",
                              second: "2-digit",
                            }
                          )}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {new Date(session.lastActivity).toISOString()}
                        </Typography>
                      </Box>
                    }
                  />
                </ListItem>

                <ListItem>
                  <ListItemIcon>
                    <MessageIcon />
                  </ListItemIcon>
                  <ListItemText
                    primary="Message Count"
                    secondary={
                      <Chip
                        label={`${session.messageCount} messages`}
                        color="primary"
                        variant="outlined"
                        size="small"
                        sx={{ mt: 0.5 }}
                      />
                    }
                  />
                </ListItem>
              </List>
            </CardContent>
          </Card>
        </Grid>

        {/* 추가 정보 */}
        <Grid size={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Additional Information
              </Typography>
              <Divider sx={{ mb: 2 }} />

              <Grid container spacing={2}>
                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                  <Box sx={{ textAlign: "center", p: 2 }}>
                    <Typography
                      variant="h5"
                      color="primary.main"
                      fontWeight="bold"
                    >
                      {session.comId}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Company ID
                    </Typography>
                  </Box>
                </Grid>

                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                  <Box sx={{ textAlign: "center", p: 2 }}>
                    <Typography
                      variant="h5"
                      color="success.main"
                      fontWeight="bold"
                    >
                      {session.companyName || "N/A"}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Company Name
                    </Typography>
                  </Box>
                </Grid>

                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                  <Box sx={{ textAlign: "center", p: 2 }}>
                    <Typography
                      variant="h5"
                      color="warning.main"
                      fontWeight="bold"
                    >
                      {Math.floor(
                        (new Date(session.lastActivity).getTime() -
                          new Date(session.regDttm).getTime()) /
                          (1000 * 60)
                      )}
                      m
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Duration (approx)
                    </Typography>
                  </Box>
                </Grid>

                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                  <Box sx={{ textAlign: "center", p: 2 }}>
                    <Typography
                      variant="h5"
                      color="info.main"
                      fontWeight="bold"
                    >
                      {session.messages?.length || 0}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Messages Loaded
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};
